import * as React from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { TaskRecord } from "@nexus/contracts";

import { SCRIPTS_BROADCAST_TASK_TYPE } from "@/features/task-progress/lib/scripts-tab-task-types";
import { personaOpportunityHubKeys } from "@/lib/queries/persona-opportunity-hub.queries";
import { personaOpportunitySetKeys } from "@/lib/queries/persona-opportunity-sets.queries";

import { detectFinishedPersonaScriptsGenerationIds } from "../lib/persona-scripts-generation-active";

/**
 * A Generate for tenants card has no client-side poll of its own (the BFF
 * publishes the jobs and returns). When a card finishes or leaves the active
 * list, refetch set detail, set scripts and the hub so the new scripts show
 * without waiting for the next live-poll tick, which stops once the card is done.
 */
export function useRefetchPersonaScriptsOnBroadcastEnd(tasks: readonly TaskRecord[]): void {
  const queryClient = useQueryClient();
  const inProgressRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    const broadcastTasks = tasks.filter(
      (task) => task.type === SCRIPTS_BROADCAST_TASK_TYPE,
    );
    const { inProgress, finishedIds } = detectFinishedPersonaScriptsGenerationIds(
      inProgressRef.current,
      broadcastTasks,
    );
    inProgressRef.current = inProgress;
    if (finishedIds.length === 0) return;
    // Set keys prefix the scripts keys, so this also refetches each set's Scripts list.
    void queryClient.invalidateQueries({ queryKey: personaOpportunitySetKeys.all });
    void queryClient.invalidateQueries({ queryKey: personaOpportunityHubKeys.all });
  }, [queryClient, tasks]);
}
